import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, AreaSeries } from 'lightweight-charts';
import { POLY_OPTIONS_ASSETS, getPolyOptionsAsset } from './polyOptions/registry';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const BINANCE_REST = 'https://api.binance.com';
const BINANCE_WS = 'wss://stream.binance.com:9443/ws';

function formatUsd(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatCompactUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return formatUsd(n, 0);
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '-';
  return `${n.toFixed(1)}¢`;
}

function formatShares(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '-';
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(4);
}

function formatEndDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
}

/** Yes → 对冲做空；No → 对冲做多 */
function isFuturesLongForOutcome(bookOutcome) {
  return bookOutcome === 'no';
}

/** 开仓价 → 目标价：多单跟涨，空单跟跌 */
function calcFuturesMovePct(open, target, isLong) {
  const o = Number(open);
  const t = Number(target);
  if (!(o > 0) || !(t > 0)) return null;
  const raw = (t - o) / o;
  return isLong ? raw : -raw;
}

/**
 * 合约逆向到多少价位时，浮亏恰好抹平 Shares 收益。
 * 多单：价格下跌抹平；空单：价格上涨抹平。
 */
function calcWipePrice({ spot, principal, leverage, polyPnl, isLong }) {
  const s = Number(spot);
  const p = Number(principal);
  const lev = Number(leverage);
  const pnl = Number(polyPnl);
  if (!(s > 0) || !(p > 0) || !(lev >= 1) || !(pnl > 0)) return null;
  const notional = p * lev;
  const adversePct = pnl / notional;
  if (!(adversePct > 0) || adversePct >= 1) {
    return { price: null, adversePct, wiped: adversePct >= 1 };
  }
  const price = isLong ? s * (1 - adversePct) : s * (1 + adversePct);
  return { price, adversePct, wiped: false };
}

/** Yes/No 结算说明（看涨触达 / 看跌触达） */
function buildSettleHint({ isYes, isUpStrike, strike, endDateLabel }) {
  const k = Number.isFinite(Number(strike)) ? formatUsd(Number(strike), 0) : '行权价';
  const before = endDateLabel && endDateLabel !== '-' ? `${endDateLabel} 前` : '到期前';
  if (isYes) {
    if (isUpStrike) {
      return `Yes：${before}现货触及/超过 ${k} 即结算兑付 $1；对冲合约做空`;
    }
    return `Yes：${before}现货触及/跌破 ${k} 即结算兑付 $1；对冲合约做空`;
  }
  if (isUpStrike) {
    return `No：${before}现货从未超过 ${k} 则到期兑付 $1；对冲合约做多`;
  }
  return `No：${before}现货从未跌破 ${k} 则到期兑付 $1；对冲合约做多`;
}

/** 按自定义 shares + 入场价，计算兑付收益（每 share 兑付 $1） */
function calcPolyByShares(shares, entryPrice) {
  const s = Number(shares);
  const e = Number(entryPrice);
  if (!(s > 0) || !(e > 0) || e >= 1) {
    return { shares: null, cost: null, payout: null, pnl: null, roi: null };
  }
  const cost = s * e;
  const payout = s * 1;
  const pnl = payout - cost;
  const roi = cost > 0 ? pnl / cost : null;
  return { shares: s, cost, payout, pnl, roi };
}

function useBinanceSpot(binanceSymbol) {
  const [spot, setSpot] = useState({ price: null, changePct: null, high: null, low: null });
  const wsRef = useRef(null);

  useEffect(() => {
    if (!binanceSymbol) return undefined;
    let cancelled = false;
    const stream = `${String(binanceSymbol).toLowerCase()}@ticker`;

    const applyTicker = (t) => {
      if (!t || cancelled) return;
      setSpot({
        price: Number(t.c ?? t.lastPrice),
        changePct: Number(t.P ?? t.priceChangePercent),
        high: Number(t.h ?? t.highPrice),
        low: Number(t.l ?? t.lowPrice),
      });
    };

    const loadRest = async () => {
      try {
        const r = await fetch(`${BINANCE_REST}/api/v3/ticker/24hr?symbol=${binanceSymbol}`);
        applyTicker(await r.json());
      } catch (_) {}
    };

    loadRest();
    const poll = window.setInterval(loadRest, 15000);

    try {
      const ws = new WebSocket(`${BINANCE_WS}/${stream}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try { applyTicker(JSON.parse(ev.data)); } catch (_) {}
      };
    } catch (_) {}

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      try { wsRef.current?.close(); } catch (_) {}
      wsRef.current = null;
    };
  }, [binanceSymbol]);

  return spot;
}

function SpotChart({ binanceSymbol, accent, livePrice }) {
  const wrapRef = useRef(null);
  const seriesRef = useRef(null);

  useEffect(() => {
    if (!wrapRef.current || !binanceSymbol) return undefined;
    const chart = createChart(wrapRef.current, {
      layout: {
        background: { color: 'transparent' },
        textColor: '#64748b',
        fontFamily: 'IBM Plex Sans, Segoe UI, sans-serif',
      },
      grid: {
        vertLines: { color: 'rgba(148,163,184,0.16)' },
        horzLines: { color: 'rgba(148,163,184,0.16)' },
      },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
      crosshair: { mode: 0 },
      height: 280,
    });
    const series = chart.addSeries(AreaSeries, {
      lineColor: accent || '#2563eb',
      topColor: `${accent || '#2563eb'}40`,
      bottomColor: `${accent || '#2563eb'}08`,
      lineWidth: 2,
      priceLineVisible: true,
      lastValueVisible: true,
    });
    seriesRef.current = series;

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BINANCE_REST}/api/v3/klines?symbol=${binanceSymbol}&interval=1d&limit=280`);
        const rows = await r.json();
        if (cancelled || !Array.isArray(rows)) return;
        series.setData(rows.map((k) => ({
          time: Math.floor(Number(k[0]) / 1000),
          value: Number(k[4]),
        })).filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value)));
        chart.timeScale().fitContent();
      } catch (_) {}
    })();

    const onResize = () => {
      if (!wrapRef.current) return;
      chart.applyOptions({ width: wrapRef.current.clientWidth });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      chart.remove();
      seriesRef.current = null;
    };
  }, [binanceSymbol, accent]);

  useEffect(() => {
    if (!seriesRef.current || !Number.isFinite(Number(livePrice))) return;
    try {
      seriesRef.current.update({ time: Math.floor(Date.now() / 1000), value: Number(livePrice) });
    } catch (_) {}
  }, [livePrice]);

  return <div ref={wrapRef} className="poly-opt-chart" />;
}

function DepthBook({ book, loading }) {
  const asks = Array.isArray(book?.asks) ? book.asks : [];
  const bids = Array.isArray(book?.bids) ? book.bids : [];
  const maxSize = Math.max(
    1,
    ...asks.map((x) => Number(x.size) || 0),
    ...bids.map((x) => Number(x.size) || 0),
  );

  return (
    <div className="poly-opt-book">
      <div className="poly-opt-book-head">
        <span>档位</span>
        <span>价格</span>
        <span>Shares</span>
        <span>总计</span>
      </div>
      <div className="poly-opt-book-asks">
        {asks.length === 0 ? (
          <div className="poly-opt-book-empty">{loading ? '加载卖单…' : '暂无卖单'}</div>
        ) : (
          asks.map((lv, i) => (
            <div key={`a-${i}`} className="poly-opt-book-row is-ask">
              <div
                className="poly-opt-book-bar"
                style={{ width: `${Math.min(100, (Number(lv.size) / maxSize) * 100)}%` }}
              />
              <span>卖{asks.length - i}</span>
              <span>{formatCents(lv.cents ?? lv.price * 100)}</span>
              <span>{formatShares(lv.size)}</span>
              <span>{formatUsd(lv.total)}</span>
            </div>
          ))
        )}
      </div>
      <div className="poly-opt-book-spread">
        {book?.bestBid && book?.bestAsk ? (
          <>
            <em>买一 {formatCents(book.bestBid.cents)}</em>
            <span>价差 {book.spread != null ? formatCents(book.spread * 100) : '-'}</span>
            <em>卖一 {formatCents(book.bestAsk.cents)}</em>
          </>
        ) : (
          <span>{loading ? '同步盘口…' : '等待盘口'}</span>
        )}
      </div>
      <div className="poly-opt-book-bids">
        {bids.length === 0 ? (
          <div className="poly-opt-book-empty">{loading ? '加载买单…' : '暂无买单'}</div>
        ) : (
          bids.map((lv, i) => (
            <div key={`b-${i}`} className="poly-opt-book-row is-bid">
              <div
                className="poly-opt-book-bar"
                style={{ width: `${Math.min(100, (Number(lv.size) / maxSize) * 100)}%` }}
              />
              <span>买{i + 1}</span>
              <span>{formatCents(lv.cents ?? lv.price * 100)}</span>
              <span>{formatShares(lv.size)}</span>
              <span>{formatUsd(lv.total)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ArbCalculator({
  selected,
  spotPrice,
  book,
  bookOutcome,
  endDateLabel,
  principal,
  setPrincipal,
  shares,
  setShares,
  leverage,
  setLeverage,
}) {
  const bestBid = book?.bestBid?.price ?? null;
  const bestAsk = book?.bestAsk?.price ?? null;
  const strike = Number(selected?.strike);
  const spot = Number(spotPrice);
  const isUpStrike = selected?.direction !== 'down';
  const isYes = bookOutcome !== 'no';
  // Yes 对冲做空；No 对冲做多
  const isFuturesLong = isFuturesLongForOutcome(bookOutcome);

  const movePct = useMemo(
    () => calcFuturesMovePct(spot, strike, isFuturesLong),
    [spot, strike, isFuturesLong],
  );

  const futures = useMemo(() => {
    const p = Number(principal);
    const lev = Number(leverage);
    if (!(p > 0) || !(lev >= 1) || movePct == null) {
      return { pnl: null, roi: null, movePct: null };
    }
    const roi = lev * movePct;
    return { pnl: p * roi, roi, movePct };
  }, [principal, leverage, movePct]);

  // 自定义 shares × 买一 / 卖一 入场
  const polyAtBid = useMemo(() => calcPolyByShares(shares, bestBid), [shares, bestBid]);
  const polyAtAsk = useMemo(() => calcPolyByShares(shares, bestAsk), [shares, bestAsk]);

  const wipe = useMemo(
    () => calcWipePrice({
      spot,
      principal,
      leverage,
      polyPnl: polyAtBid.pnl,
      isLong: isFuturesLong,
    }),
    [spot, principal, leverage, polyAtBid.pnl, isFuturesLong],
  );

  const outcomeLabel = isYes ? 'Yes' : 'No';
  const resolveHint = buildSettleHint({
    isYes,
    isUpStrike,
    strike,
    endDateLabel,
  });
  const futLabel = isFuturesLong ? '多' : '空';
  const futDir = isFuturesLong ? 'Long' : 'Short';

  return (
    <section className="poly-opt-arb">
      <div className="poly-opt-arb-head">
        <h3>收益 / 期权套利计算器</h3>
        <p>
          {selected
            ? `${selected.label} · ${outcomeLabel} · 现货 ${formatUsd(spot)} → ${formatUsd(strike)}（合约${futLabel}）`
            : '先点左侧行权价'}
        </p>
      </div>

      <div className="poly-opt-arb-inputs">
        <label>
          <span>本金 (USD)</span>
          <input
            type="number"
            min="1"
            step="10"
            value={principal}
            onChange={(e) => setPrincipal(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          <span>Shares（自定义）</span>
          <input
            type="number"
            min="1"
            step="1"
            value={shares}
            onChange={(e) => setShares(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label className="poly-opt-arb-lev-wrap">
          <span>合约杠杆 {leverage}x</span>
          <input
            type="range"
            min="1"
            max="20"
            step="1"
            value={leverage}
            onChange={(e) => setLeverage(Number(e.target.value) || 1)}
          />
          <div className="poly-opt-arb-lev">
            {[1, 5, 10, 15, 20].map((v) => (
              <button key={v} type="button" className={leverage === v ? 'active' : ''} onClick={() => setLeverage(v)}>
                {v}x
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className="poly-opt-arb-quote">
        <div>
          <span>当前买一</span>
          <strong>{bestBid != null ? formatCents(bestBid * 100) : '-'}</strong>
          <small>{outcomeLabel} token</small>
        </div>
        <div>
          <span>当前卖一</span>
          <strong>{bestAsk != null ? formatCents(bestAsk * 100) : '-'}</strong>
          <small>真实买入参考</small>
        </div>
        <div>
          <span>合约到行权价</span>
          <strong>{movePct == null ? '-' : formatPct(movePct * 100)}</strong>
          <small>{isFuturesLong ? '做多' : '做空'} → {formatUsd(strike, 0)}</small>
        </div>
      </div>

      <p className="poly-opt-arb-hint">{resolveHint}</p>

      <div className="poly-opt-arb-grid">
        <article className="poly-opt-arb-card is-poly">
          <header>Poly · {outcomeLabel} · 按买一入场</header>
          <div><span>入场</span><strong>{bestBid != null ? formatCents(bestBid * 100) : '-'}</strong></div>
          <div><span>Shares</span><strong>{formatShares(shares)}</strong></div>
          <div><span>成本</span><strong>{polyAtBid.cost == null ? '-' : formatUsd(polyAtBid.cost)}</strong></div>
          <div><span>兑付收益</span><strong className={polyAtBid.pnl >= 0 ? 'up' : 'down'}>{polyAtBid.pnl == null ? '-' : formatUsd(polyAtBid.pnl)}</strong></div>
          <div><span>收益率</span><strong className={polyAtBid.roi >= 0 ? 'up' : 'down'}>{polyAtBid.roi == null ? '-' : formatPct(polyAtBid.roi * 100)}</strong></div>
        </article>

        <article className="poly-opt-arb-card is-poly-ask">
          <header>Poly · {outcomeLabel} · 按卖一买入</header>
          <div><span>入场</span><strong>{bestAsk != null ? formatCents(bestAsk * 100) : '-'}</strong></div>
          <div><span>Shares</span><strong>{formatShares(shares)}</strong></div>
          <div><span>成本</span><strong>{polyAtAsk.cost == null ? '-' : formatUsd(polyAtAsk.cost)}</strong></div>
          <div><span>兑付收益</span><strong className={polyAtAsk.pnl >= 0 ? 'up' : 'down'}>{polyAtAsk.pnl == null ? '-' : formatUsd(polyAtAsk.pnl)}</strong></div>
          <div><span>收益率</span><strong className={polyAtAsk.roi >= 0 ? 'up' : 'down'}>{polyAtAsk.roi == null ? '-' : formatPct(polyAtAsk.roi * 100)}</strong></div>
        </article>

        <article className="poly-opt-arb-card is-fut">
          <header>合约 · {leverage}x {futLabel}（{outcomeLabel} 对冲）</header>
          <div><span>方向</span><strong>{futDir} → {formatUsd(strike, 0)}</strong></div>
          <div><span>价格变动</span><strong>{movePct == null ? '-' : formatPct(movePct * 100)}</strong></div>
          <div><span>合约收益</span><strong className={futures.pnl >= 0 ? 'up' : 'down'}>{futures.pnl == null ? '-' : formatUsd(futures.pnl)}</strong></div>
          <div><span>收益率</span><strong className={futures.roi >= 0 ? 'up' : 'down'}>{futures.roi == null ? '-' : formatPct(futures.roi * 100)}</strong></div>
        </article>
      </div>

      <div className="poly-opt-arb-compare">
        <div>
          <span>买价 Shares 收益</span>
          <strong className={polyAtBid.pnl == null ? '' : (polyAtBid.pnl >= 0 ? 'up' : 'down')}>
            {polyAtBid.pnl == null ? '-' : formatUsd(polyAtBid.pnl)}
          </strong>
          <small>{formatShares(shares)} × 买一 {bestBid != null ? formatCents(bestBid * 100) : '-'}</small>
        </div>
        <div>
          <span>合约收益（{futLabel}）</span>
          <strong className={futures.pnl == null ? '' : (futures.pnl >= 0 ? 'up' : 'down')}>
            {futures.pnl == null ? '-' : formatUsd(futures.pnl)}
          </strong>
          <small>{leverage}x · 本金 {formatUsd(principal, 0)} · {outcomeLabel}→{futLabel}</small>
        </div>
      </div>

      <div className="poly-opt-arb-wipe">
        <span>{isFuturesLong ? '合约跌至抹平' : '合约涨至抹平'}</span>
        <strong>
          {wipe == null
            ? '-'
            : wipe.wiped
              ? '名义仓位不足以抹平（浮亏上限低于 Shares 收益）'
              : wipe.price == null
                ? '-'
                : formatUsd(wipe.price)}
        </strong>
        <small>
          {wipe == null || wipe.wiped || wipe.price == null
            ? `以买价 Shares 收益为缓冲，${futLabel}单逆向至此价开始净亏损`
            : `相对现货 ${isFuturesLong ? '回撤' : '反弹'} ${formatPct(wipe.adversePct * 100)} · 浮亏 = 买价 Shares 收益后开始净亏`}
        </small>
      </div>
    </section>
  );
}

/**
 * T 买入 / 分批成交看板：自定义合约开仓价 + 自定义 Shares 入场价，不绑定现价盘口。
 */
function CustomTBoard({ selected, spotPrice, book, bookOutcome, endDateLabel }) {
  const strikeDefault = Number(selected?.strike);
  const spot = Number(spotPrice);
  const bestBid = book?.bestBid?.price ?? null;
  const bestAsk = book?.bestAsk?.price ?? null;
  const isUpStrike = selected?.direction !== 'down';
  const isYes = bookOutcome !== 'no';
  const isFuturesLong = isFuturesLongForOutcome(bookOutcome);
  const futLabel = isFuturesLong ? '多' : '空';
  const outcomeLabel = isYes ? 'Yes' : 'No';

  const [openPrice, setOpenPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [shareCents, setShareCents] = useState('');
  const [tShares, setTShares] = useState(1000);
  const [tPrincipal, setTPrincipal] = useState(1000);
  const [tLeverage, setTLeverage] = useState(5);

  // 有现货时预填开仓价（仅空值）；有买一时预填 Shares 价（仅空值）
  useEffect(() => {
    if (!(Number.isFinite(spot) && spot > 0)) return;
    setOpenPrice((prev) => (prev === '' ? String(Math.round(spot * 100) / 100) : prev));
  }, [spot]);

  useEffect(() => {
    if (bestBid == null) return;
    setShareCents((prev) => (prev === '' ? String((bestBid * 100).toFixed(1)) : prev));
  }, [bestBid]);

  // 切换行权价时，同步目标价（开仓价 / shares 价保留用户自定义）
  useEffect(() => {
    if (Number.isFinite(strikeDefault) && strikeDefault > 0) {
      setTargetPrice(String(strikeDefault));
    }
  }, [selected?.id, strikeDefault]);

  const fillFromMarket = () => {
    if (Number.isFinite(spot) && spot > 0) setOpenPrice(String(Math.round(spot * 100) / 100));
    if (Number.isFinite(strikeDefault) && strikeDefault > 0) setTargetPrice(String(strikeDefault));
    if (bestBid != null) setShareCents(String((bestBid * 100).toFixed(1)));
    else if (bestAsk != null) setShareCents(String((bestAsk * 100).toFixed(1)));
  };

  const open = Number(openPrice);
  const target = Number(targetPrice);
  const entryShare = Number(shareCents) / 100; // ¢ → 0~1

  const movePct = useMemo(
    () => calcFuturesMovePct(open, target, isFuturesLong),
    [open, target, isFuturesLong],
  );

  const futures = useMemo(() => {
    const p = Number(tPrincipal);
    const lev = Number(tLeverage);
    if (!(p > 0) || !(lev >= 1) || movePct == null) {
      return { pnl: null, roi: null };
    }
    const roi = lev * movePct;
    return { pnl: p * roi, roi };
  }, [tPrincipal, tLeverage, movePct]);

  const poly = useMemo(
    () => calcPolyByShares(tShares, entryShare),
    [tShares, entryShare],
  );

  const wipe = useMemo(
    () => calcWipePrice({
      spot: open,
      principal: tPrincipal,
      leverage: tLeverage,
      polyPnl: poly.pnl,
      isLong: isFuturesLong,
    }),
    [open, tPrincipal, tLeverage, poly.pnl, isFuturesLong],
  );

  const edge = poly.pnl != null && futures.pnl != null ? poly.pnl - futures.pnl : null;
  const resolveHint = buildSettleHint({
    isYes,
    isUpStrike,
    strike: Number.isFinite(target) && target > 0 ? target : strikeDefault,
    endDateLabel,
  });

  return (
    <section className="poly-opt-arb poly-opt-tboard">
      <div className="poly-opt-arb-head">
        <h3>T 买入自定义看板</h3>
        <p>
          分批成交价 ≠ 现价时用这里：自定义合约开仓价 + Shares 入场价。
          {selected ? ` 当前档 ${selected.label} · ${outcomeLabel}→合约${futLabel}` : ''}
        </p>
      </div>

      <div className="poly-opt-tboard-actions">
        <button type="button" onClick={fillFromMarket}>从盘口同步开仓价 / Shares 价</button>
        <small>
          现货 {formatUsd(spot)} · 买一 {bestBid != null ? formatCents(bestBid * 100) : '-'} · 卖一{' '}
          {bestAsk != null ? formatCents(bestAsk * 100) : '-'}
        </small>
      </div>

      <p className="poly-opt-arb-hint">{resolveHint}</p>

      <div className="poly-opt-arb-inputs poly-opt-tboard-inputs">
        <label>
          <span>合约开仓价 (USD)</span>
          <input
            type="number"
            min="1"
            step="1"
            value={openPrice}
            onChange={(e) => setOpenPrice(e.target.value)}
            placeholder="例如 78500"
          />
        </label>
        <label>
          <span>合约目标价 (USD)</span>
          <input
            type="number"
            min="1"
            step="1"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            placeholder="例如 95000"
          />
        </label>
        <label>
          <span>Shares 入场价 (¢)</span>
          <input
            type="number"
            min="0.1"
            max="99.9"
            step="0.1"
            value={shareCents}
            onChange={(e) => setShareCents(e.target.value)}
            placeholder="例如 39.0"
          />
        </label>
        <label>
          <span>Shares 数量</span>
          <input
            type="number"
            min="1"
            step="1"
            value={tShares}
            onChange={(e) => setTShares(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          <span>合约本金 (USD)</span>
          <input
            type="number"
            min="1"
            step="10"
            value={tPrincipal}
            onChange={(e) => setTPrincipal(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label className="poly-opt-arb-lev-wrap">
          <span>合约杠杆 {tLeverage}x（{futLabel}）</span>
          <input
            type="range"
            min="1"
            max="20"
            step="1"
            value={tLeverage}
            onChange={(e) => setTLeverage(Number(e.target.value) || 1)}
          />
          <div className="poly-opt-arb-lev">
            {[1, 5, 10, 15, 20].map((v) => (
              <button
                key={v}
                type="button"
                className={tLeverage === v ? 'active' : ''}
                onClick={() => setTLeverage(v)}
              >
                {v}x
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className="poly-opt-arb-quote">
        <div>
          <span>开仓 → 目标</span>
          <strong>
            {open > 0 && target > 0 ? `${formatUsd(open, 0)} → ${formatUsd(target, 0)}` : '-'}
          </strong>
          <small>{futLabel} · {movePct == null ? '-' : formatPct(movePct * 100)}</small>
        </div>
        <div>
          <span>Shares 入场</span>
          <strong>{Number.isFinite(entryShare) && entryShare > 0 ? formatCents(entryShare * 100) : '-'}</strong>
          <small>{outcomeLabel} · {formatShares(tShares)} 份</small>
        </div>
        <div>
          <span>Shares 成本</span>
          <strong>{poly.cost == null ? '-' : formatUsd(poly.cost)}</strong>
          <small>兑付面值 {poly.payout == null ? '-' : formatUsd(poly.payout)}</small>
        </div>
      </div>

      <div className="poly-opt-arb-grid">
        <article className="poly-opt-arb-card is-poly">
          <header>Poly · {outcomeLabel} · 自定义 Shares</header>
          <div><span>入场</span><strong>{Number.isFinite(entryShare) && entryShare > 0 ? formatCents(entryShare * 100) : '-'}</strong></div>
          <div><span>Shares</span><strong>{formatShares(tShares)}</strong></div>
          <div><span>兑付收益</span><strong className={poly.pnl >= 0 ? 'up' : 'down'}>{poly.pnl == null ? '-' : formatUsd(poly.pnl)}</strong></div>
          <div><span>收益率</span><strong className={poly.roi >= 0 ? 'up' : 'down'}>{poly.roi == null ? '-' : formatPct(poly.roi * 100)}</strong></div>
        </article>

        <article className="poly-opt-arb-card is-fut">
          <header>合约 · {tLeverage}x {futLabel}（{outcomeLabel} 对冲）</header>
          <div><span>开仓</span><strong>{open > 0 ? formatUsd(open) : '-'}</strong></div>
          <div><span>目标</span><strong>{target > 0 ? formatUsd(target) : '-'}</strong></div>
          <div><span>合约收益</span><strong className={futures.pnl >= 0 ? 'up' : 'down'}>{futures.pnl == null ? '-' : formatUsd(futures.pnl)}</strong></div>
          <div><span>收益率</span><strong className={futures.roi >= 0 ? 'up' : 'down'}>{futures.roi == null ? '-' : formatPct(futures.roi * 100)}</strong></div>
        </article>
      </div>

      <div className="poly-opt-arb-compare">
        <div>
          <span>自定义 Shares 收益</span>
          <strong className={poly.pnl == null ? '' : (poly.pnl >= 0 ? 'up' : 'down')}>
            {poly.pnl == null ? '-' : formatUsd(poly.pnl)}
          </strong>
          <small>{formatShares(tShares)} × {Number.isFinite(Number(shareCents)) ? formatCents(Number(shareCents)) : '-'}</small>
        </div>
        <div>
          <span>自定义开仓合约收益（{futLabel}）</span>
          <strong className={futures.pnl == null ? '' : (futures.pnl >= 0 ? 'up' : 'down')}>
            {futures.pnl == null ? '-' : formatUsd(futures.pnl)}
          </strong>
          <small>
            {edge == null
              ? `${tLeverage}x · 本金 ${formatUsd(tPrincipal, 0)} · ${outcomeLabel}→${futLabel}`
              : `差值 ${edge >= 0 ? '+' : ''}${formatUsd(edge)}（Shares − 合约）`}
          </small>
        </div>
      </div>

      <div className="poly-opt-arb-wipe">
        <span>{isFuturesLong ? '自开仓价跌至抹平' : '自开仓价涨至抹平'}</span>
        <strong>
          {wipe == null
            ? '-'
            : wipe.wiped
              ? '名义仓位不足以抹平（浮亏上限低于 Shares 收益）'
              : wipe.price == null
                ? '-'
                : formatUsd(wipe.price)}
        </strong>
        <small>
          {wipe == null || wipe.wiped || wipe.price == null
            ? `以自定义 Shares 收益为缓冲，${futLabel}单相对开仓价逆向至此开始净亏`
            : `相对开仓价 ${isFuturesLong ? '回撤' : '反弹'} ${formatPct(wipe.adversePct * 100)} · 浮亏 = Shares 收益后开始净亏`}
        </small>
      </div>
    </section>
  );
}

export default function PolyOptionsPage() {
  const [assetId, setAssetId] = useState('btc');
  const asset = useMemo(() => getPolyOptionsAsset(assetId), [assetId]);
  const spot = useBinanceSpot(asset.enabled ? asset.binanceSymbol : '');

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sideFilter, setSideFilter] = useState('up');
  const [selectedId, setSelectedId] = useState(null);
  const [bookOutcome, setBookOutcome] = useState('yes');
  const [book, setBook] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookError, setBookError] = useState('');
  const [principal, setPrincipal] = useState(1000);
  const [shares, setShares] = useState(1000);
  const [leverage, setLeverage] = useState(5);

  const loadBoard = useCallback(async () => {
    if (!asset.enabled) {
      setBoard(null);
      setError(asset.note || '该资产尚未接入');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(api(`/api/poly-options?asset=${asset.id}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `加载失败 ${resp.status}`);
      setBoard(body.data || null);
      const first = (body.data?.ups?.[0] || body.data?.options?.[0]) || null;
      setSelectedId((prev) => {
        if (prev && (body.data?.options || []).some((o) => o.id === prev)) return prev;
        return first?.id || null;
      });
    } catch (err) {
      setError(err?.message || '加载失败');
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [asset]);

  useEffect(() => {
    loadBoard();
    if (!asset.enabled) return undefined;
    const timer = window.setInterval(loadBoard, 30000);
    return () => window.clearInterval(timer);
  }, [loadBoard, asset.enabled]);

  const rows = useMemo(() => {
    const all = board?.options || [];
    if (sideFilter === 'up') return all.filter((o) => o.direction === 'up');
    if (sideFilter === 'down') return all.filter((o) => o.direction === 'down');
    return all;
  }, [board, sideFilter]);

  const selected = useMemo(
    () => (board?.options || []).find((o) => o.id === selectedId) || rows[0] || null,
    [board, selectedId, rows],
  );

  const activeTokenId = bookOutcome === 'no' ? selected?.noTokenId : selected?.yesTokenId;

  const loadBook = useCallback(async () => {
    if (!activeTokenId) {
      setBook(null);
      setBookError(selected ? '该选项缺少 CLOB token' : '');
      return;
    }
    setBookLoading(true);
    setBookError('');
    try {
      const resp = await fetch(api(`/api/poly-options/book?token_id=${encodeURIComponent(activeTokenId)}&depth=15`), {
        cache: 'no-store',
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `盘口失败 ${resp.status}`);
      setBook(body.data || null);
    } catch (err) {
      setBook(null);
      setBookError(err?.message || '盘口加载失败');
    } finally {
      setBookLoading(false);
    }
  }, [activeTokenId, selected]);

  useEffect(() => {
    loadBook();
    if (!activeTokenId) return undefined;
    const timer = window.setInterval(loadBook, 8000);
    return () => window.clearInterval(timer);
  }, [loadBook, activeTokenId]);

  const title = asset.titleZh || board?.event?.title || `${asset.label} 价格目标`;
  const changeUp = Number(spot.changePct) >= 0;
  const distPct = Number.isFinite(spot.price) && Number.isFinite(selected?.strike)
    ? ((selected.strike - spot.price) / spot.price) * 100
    : null;

  return (
    <div className="poly-opt-page primary-arb-page" style={{ '--poly-accent': asset.accent || '#2563eb' }}>
      <section className="cxmt-hero poly-opt-hero">
        <div className="poly-opt-hero-main">
          {board?.event?.image ? (
            <img src={board.event.image} alt="" className="poly-opt-hero-icon" />
          ) : (
            <div className="poly-opt-hero-icon poly-opt-hero-fallback">{asset.symbol.slice(0, 1)}</div>
          )}
          <div>
            <span className="cxmt-eyebrow">{asset.category}</span>
            <h1>{title}</h1>
            <p>
              把 Polymarket 价格目标当成二元期权看板：左侧行权价，右侧实时买卖盘（价格 / Shares / 总计）。
              现货走 Binance，盘口走 CLOB。后续 HYPE / LIT 只需补注册表。
            </p>
          </div>
        </div>
        <div className="cxmt-hero-actions">
          <button type="button" onClick={loadBoard} disabled={loading || !asset.enabled}>
            {loading ? '刷新中…' : '刷新盘口'}
          </button>
          {asset.polyUrl ? (
            <a href={asset.polyUrl} target="_blank" rel="noreferrer">打开 Polymarket</a>
          ) : null}
        </div>
      </section>

      <div className="poly-opt-asset-tabs">
        {POLY_OPTIONS_ASSETS.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`poly-opt-asset-tab ${assetId === a.id ? 'active' : ''} ${a.enabled ? '' : 'is-disabled'}`}
            onClick={() => setAssetId(a.id)}
          >
            <strong>{a.symbol}</strong>
            <span>{a.enabled ? a.label : '待接入'}</span>
          </button>
        ))}
      </div>

      <section className="poly-opt-spotbar primary-arb-spotbar">
        <div>
          <span>{asset.symbol} 现货</span>
          <strong>{formatUsd(spot.price)}</strong>
          <em className={changeUp ? 'up' : 'down'}>{formatPct(spot.changePct)}</em>
        </div>
        <div>
          <span>24h 高 / 低</span>
          <strong>{formatUsd(spot.high)} · {formatUsd(spot.low)}</strong>
        </div>
        <div>
          <span>事件成交量</span>
          <strong>{formatCompactUsd(board?.event?.volume)}</strong>
        </div>
        <div>
          <span>到期</span>
          <strong>{formatEndDate(board?.event?.endDate)}</strong>
        </div>
      </section>

      <div className="poly-opt-layout">
        <div className="poly-opt-main">
          <section className="primary-arb-chart-card poly-opt-chart-card">
            {asset.enabled ? (
              <SpotChart binanceSymbol={asset.binanceSymbol} accent={asset.accent || '#2563eb'} livePrice={spot.price} />
            ) : (
              <div className="poly-opt-empty">该资产尚未配置 Polymarket 事件</div>
            )}
          </section>

          <section className="poly-opt-strikes primary-arb-results">
            <div className="poly-opt-strikes-head">
              <div>
                <h2>行权价列表</h2>
                <p>点击任一档，右侧显示该 outcome 的买卖盘深度</p>
              </div>
              <div className="poly-opt-filter">
                <button type="button" className={sideFilter === 'up' ? 'active' : ''} onClick={() => setSideFilter('up')}>看涨 ↑</button>
                <button type="button" className={sideFilter === 'down' ? 'active' : ''} onClick={() => setSideFilter('down')}>看跌 ↓</button>
                <button type="button" className={sideFilter === 'all' ? 'active' : ''} onClick={() => setSideFilter('all')}>全部</button>
              </div>
            </div>

            {error && <div className="primary-arb-message is-error">{error}</div>}

            <div className="poly-opt-table">
              {rows.length === 0 ? (
                <div className="poly-opt-empty">{loading ? '加载盘口中…' : '暂无行权价'}</div>
              ) : (
                rows.map((row) => {
                  const active = selected?.id === row.id;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className={`poly-opt-row ${active ? 'active' : ''} ${row.direction}`}
                      onClick={() => {
                        setSelectedId(row.id);
                        setBookOutcome('yes');
                      }}
                    >
                      <div className="poly-opt-row-main">
                        <strong>{row.label}</strong>
                        <small>{formatCompactUsd(row.volume)} 交易量</small>
                      </div>
                      <div className="poly-opt-row-prob">{Math.round(Number(row.probability) || 0)}%</div>
                      <div className="poly-opt-row-actions">
                        <span className="poly-opt-chip yes">是 {formatCents(row.yesCents)}</span>
                        <span className="poly-opt-chip no">否 {formatCents(row.noCents)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <aside className="poly-opt-side">
          <section className="poly-opt-trade-card">
            <div className="poly-opt-side-head">
              <div>
                <h2>{selected ? selected.label : '选择行权价'}</h2>
                <p>
                  {selected
                    ? `${bookOutcome === 'yes'
                      ? (selected.direction === 'down'
                        ? `Yes：到期前触及/跌破则兑付 · 对冲做空`
                        : `Yes：到期前触及/超过则兑付 · 对冲做空`)
                      : (selected.direction === 'down'
                        ? `No：到期前从未跌破则兑付 · 对冲做多`
                        : `No：到期前从未超过则兑付 · 对冲做多`)
                    } · 距现货 ${distPct == null ? '-' : `${distPct.toFixed(1)}%`}${board?.event?.endDate ? ` · 截止 ${formatEndDate(board.event.endDate)}` : ''}`
                    : '从左侧点选一档查看买卖盘'}
                </p>
              </div>
              <button type="button" className="poly-opt-refresh-btn" disabled={bookLoading || !activeTokenId} onClick={loadBook}>
                {bookLoading ? '…' : '刷新'}
              </button>
            </div>

            {selected && (
              <div className="poly-opt-side-toggle">
                <button
                  type="button"
                  className={`yes ${bookOutcome === 'yes' ? 'active' : ''}`}
                  onClick={() => setBookOutcome('yes')}
                >
                  <span>Yes</span>
                  <em>{formatCents(selected.yesCents)}</em>
                </button>
                <button
                  type="button"
                  className={`no ${bookOutcome === 'no' ? 'active' : ''}`}
                  onClick={() => setBookOutcome('no')}
                >
                  <span>No</span>
                  <em>{formatCents(selected.noCents)}</em>
                </button>
              </div>
            )}

            {bookError && <div className="primary-arb-message is-error">{bookError}</div>}

            {book?.summary && (
              <div className="poly-opt-depth-summary">
                <div>
                  <span>买盘</span>
                  <strong>{formatShares(book.summary.bidShares)}</strong>
                  <small>{formatUsd(book.summary.bidNotional)}</small>
                </div>
                <div>
                  <span>卖盘</span>
                  <strong>{formatShares(book.summary.askShares)}</strong>
                  <small>{formatUsd(book.summary.askNotional)}</small>
                </div>
                <div>
                  <span>价差</span>
                  <strong>{book.spread != null ? formatCents(book.spread * 100) : '-'}</strong>
                  <small>{book.summary.bidLevels}+{book.summary.askLevels} 档</small>
                </div>
              </div>
            )}

            <DepthBook book={book} loading={bookLoading} />

            <ArbCalculator
              selected={selected}
              spotPrice={spot.price}
              book={book}
              bookOutcome={bookOutcome}
              endDateLabel={formatEndDate(board?.event?.endDate)}
              principal={principal}
              setPrincipal={setPrincipal}
              shares={shares}
              setShares={setShares}
              leverage={leverage}
              setLeverage={setLeverage}
            />

            <CustomTBoard
              selected={selected}
              spotPrice={spot.price}
              book={book}
              bookOutcome={bookOutcome}
              endDateLabel={formatEndDate(board?.event?.endDate)}
            />

            {(selected?.polyMarketUrl || asset.polyUrl) ? (
              <a
                className="poly-opt-trade-cta"
                href={selected?.polyMarketUrl || asset.polyUrl}
                target="_blank"
                rel="noreferrer"
              >
                去 Polymarket 交易 {bookOutcome === 'yes' ? 'Yes' : 'No'}
              </a>
            ) : null}
          </section>
        </aside>
      </div>
    </div>
  );
}
